/**
 * The marks on the chrome bar — sound, the sheet, the Ledger, the realm.
 *
 * The bar was nine characters at the same size, in the same colour, at the same
 * weight: `❚❚ « ×1 » ♪ ≣ ✦ ☰ ⚑ ?`. Two of them, `≣` (the sound and display
 * sliders) and `☰` (the Ledger), are the same stack of horizontal lines and sat
 * two apart; `✦` stood for the character sheet, which it has no reason to. It is
 * the resource strip's problem again — a row of dingbats is a row nobody learns
 * — and it gets the same answer.
 *
 * `currentColor` throughout, so the bar's own hover and active states drive the
 * colour and there is one place that decides what a lit button looks like.
 *
 * **The time controls are deliberately still characters.** `❚❚`, `▶`, `«` and
 * `»` are not dingbats, they are the universal transport symbols; a player has
 * known what they mean since before they opened this game, and drawing bespoke
 * versions of them would be the only icons on the bar that had to be learned.
 */

export const CHROME_MARKS = {
  /** A horn, mouth open. Sound is on. */
  sound:
    '<path fill="currentColor" d="M2 6.4h3.2l4.4-3.6v10.4L5.2 9.6H2z"/>' +
    '<path fill="currentColor" d="M11.2 5.4a4.6 4.6 0 0 1 0 5.2l1.3.9a6.2 6.2 0 0 0 0-7z" opacity="0.8"/>' +
    '<path fill="currentColor" d="M13.4 3.2a8 8 0 0 1 0 9.6l1.3.9a9.6 9.6 0 0 0 0-11.4z" opacity="0.5"/>',
  /** The same horn, struck through. Sound is off. */
  mute:
    '<path fill="currentColor" d="M2 6.4h3.2l4.4-3.6v10.4L5.2 9.6H2z"/>' +
    '<path fill="currentColor" d="M11.1 6.3 12.2 5.2l1.7 1.7 1.7-1.7 1.1 1.1-1.7 1.7 1.7 1.7-1.1 1.1-1.7-1.7-1.7 1.7-1.1-1.1 1.7-1.7z" transform="translate(-1.4 0)"/>',
  /** Three sliders. The mix, the shake, the floating numbers. */
  sliders:
    '<path fill="currentColor" d="M1.6 3.4h12.8v1.5H1.6zM1.6 7.3h12.8v1.5H1.6zM1.6 11.2h12.8v1.5H1.6z" opacity="0.55"/>' +
    '<path fill="currentColor" d="M9.4 2.1h1.9v4.1H9.4zM3.6 6h1.9v4.1H3.6zM10.9 9.9h1.9V14h-1.9z"/>',
  /**
   * A helmet, in profile. The character sheet.
   *
   * Not a person and not a star: the sheet is about the one soldier in the realm
   * who gets better on purpose, and a helm is what the game already draws on him.
   */
  helm:
    '<path fill="currentColor" d="M8 1.6c3.4 0 5.6 2.3 5.6 5.6v6.8h-3V9.4H5.4V14h-3V7.2C2.4 3.9 4.6 1.6 8 1.6z"/>' +
    '<path fill="#12100e" d="M7.3 3.4h1.4v4.4H7.3z" opacity="0.85"/>',
  /** An open book. The Ledger — four pages of things somebody has counted. */
  book:
    '<path fill="currentColor" d="M1.4 3.2c2.3 0 4.5.5 6.1 1.5v9.1c-1.6-1-3.8-1.5-6.1-1.5z"/>' +
    '<path fill="currentColor" d="M14.6 3.2c-2.3 0-4.5.5-6.1 1.5v9.1c1.6-1 3.8-1.5 6.1-1.5z" opacity="0.72"/>',
  /** A banner on a pole. Out of the city and onto the map. */
  banner:
    '<path fill="currentColor" d="M2.6 1.4h1.6v13.2H2.6z" opacity="0.8"/>' +
    '<path fill="currentColor" d="M4.2 2.2h9.6l-2.2 3 2.2 3H4.2z"/>',
} as const;

/** One mark as an inline `<svg>`, sized for the chrome bar. */
export function chromeMark(k: keyof typeof CHROME_MARKS, px = 16): string {
  return `<svg viewBox="0 0 16 16" width="${px}" height="${px}" aria-hidden="true">${CHROME_MARKS[k]}</svg>`;
}
